import { useEffect, useState } from "react";
import { Sparkles, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorText } from "@/lib/api";

const empty = { word: "", subject: "Italiano", definition: "", examples: ["", "", ""], curiosity: "" };
const field = "mt-1 w-full rounded-xl border-2 border-[var(--ink)] px-3 py-2 font-semibold outline-none focus:bg-[var(--paper)]";

export const TermForm = ({ open, onClose, term, subjects, onSaved }) => {
  const [f, setF] = useState(empty);
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setF(term ? { ...term, examples: term.examples.length ? term.examples : [""] } : empty);
  }, [open, term]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setEx = (i, v) => set("examples", f.examples.map((e, j) => (j === i ? v : e)));

  const draft = async () => {
    if (!f.word.trim()) return toast.error("Scrivi prima la parola");
    setDrafting(true);
    try {
      const { data } = await api.post("/ai/draft", { word: f.word, subject: f.subject });
      setF((p) => ({ ...p, ...data }));
      toast.success("Bozza generata: controllala e modificala se serve");
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setDrafting(false);
    }
  };

  const save = async () => {
    if (!f.word.trim() || !f.definition.trim()) return toast.error("Parola e definizione sono obbligatorie");
    setSaving(true);
    const body = { word: f.word.trim(), subject: f.subject, definition: f.definition.trim(), curiosity: f.curiosity || "", examples: f.examples.map((e) => e.trim()).filter(Boolean) };
    try {
      term ? await api.put(`/terms/${term.id}`, body) : await api.post("/terms", body);
      toast.success("Termine salvato");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl border-[2.5px] border-[var(--ink)] bg-white sm:max-w-2xl" data-testid="term-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{term ? "Modifica termine" : "Nuovo termine"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-[1fr_200px]">
          <div>
            <label className="text-sm font-extrabold">Parola</label>
            <input value={f.word} onChange={(e) => set("word", e.target.value)} data-testid="term-form-word-input" className={field} />
          </div>
          <div>
            <label className="text-sm font-extrabold">Materia</label>
            <select value={f.subject} onChange={(e) => set("subject", e.target.value)} data-testid="term-form-subject-select" className={field}>
              {subjects.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <button onClick={draft} disabled={drafting} data-testid="term-form-ai-draft-button" className="ink-box-sm ink-press flex items-center justify-center gap-2 rounded-full bg-[var(--sun)] py-2.5 font-extrabold disabled:opacity-60">
          {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {drafting ? "L'AI sta scrivendo…" : "Genera definizione ed esempi con AI"}
        </button>
        <div>
          <label className="text-sm font-extrabold">Definizione semplice</label>
          <textarea rows={3} value={f.definition} onChange={(e) => set("definition", e.target.value)} data-testid="term-form-definition-input" className={field} />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-extrabold">Frasi di esempio</label>
          {f.examples.map((ex, i) => (
            <div key={i} className="flex gap-2">
              <input value={ex} onChange={(e) => setEx(i, e.target.value)} data-testid={`term-form-example-input-${i}`} className={field + " mt-0"} />
              <button onClick={() => set("examples", f.examples.filter((_, j) => j !== i))} data-testid={`term-form-remove-example-${i}`} className="rounded-xl border-2 border-[var(--ink)] px-3 hover:bg-red-100"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={() => set("examples", [...f.examples, ""])} data-testid="term-form-add-example" className="flex items-center gap-1 text-sm font-extrabold hover:underline"><Plus className="h-4 w-4" /> Aggiungi frase</button>
        </div>
        <div>
          <label className="text-sm font-extrabold">Curiosità (facoltativa)</label>
          <input value={f.curiosity} onChange={(e) => set("curiosity", e.target.value)} data-testid="term-form-curiosity-input" className={field} />
        </div>
        <button onClick={save} disabled={saving} data-testid="term-form-save-button" className="ink-box-sm ink-press flex items-center justify-center gap-2 rounded-full bg-[var(--sky)] py-3 font-extrabold text-white disabled:opacity-60">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva termine
        </button>
      </DialogContent>
    </Dialog>
  );
};
